import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { BarChart } from '../components/charts/BarChart';
import { interpolateColors } from '../components/charts/colors';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

interface KpiItem {
  label: string;
  value: number;
  type: 'money' | 'count' | 'percent';
}

interface AllocationItem {
  label: string;
  value: number;
  breach?: boolean;
}

interface InvestmentRiskFlags {
  leases_expiring_soon: number;
  insurance_expiring_soon: number;
  npl_loans: number;
  idle_cash_holdings: number;
  equity_concentration_breaches: number;
  offshore_country_concentration_breaches: number;
}

interface InvestmentsStats {
  kpis: KpiItem[];
  asset_allocation: AllocationItem[];
  investment_risk_flags: InvestmentRiskFlags;
}

// Human-readable label for each investment_risk_flags key, in display
// order — only flags with a non-zero count are shown.
const RISK_FLAG_LABELS: Array<{ key: keyof InvestmentRiskFlags; singular: string; plural: string }> = [
  { key: 'leases_expiring_soon', singular: 'lease expiring within 90 days', plural: 'leases expiring within 90 days' },
  { key: 'insurance_expiring_soon', singular: 'insurance policy expiring within 90 days', plural: 'insurance policies expiring within 90 days' },
  { key: 'npl_loans', singular: 'loan NPL', plural: 'loans NPL' },
  { key: 'idle_cash_holdings', singular: 'idle-cash holding', plural: 'idle-cash holdings' },
  { key: 'equity_concentration_breaches', singular: 'equity concentration breach', plural: 'equity concentration breaches' },
  { key: 'offshore_country_concentration_breaches', singular: 'offshore country concentration breach', plural: 'offshore country concentration breaches' },
];

const GREEN_RAMP_STOPS = ['#60aa45', '#3f7a29', '#2c591c'];

export function InvestmentsDashboard() {
  const { hasPermission } = useAuth();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [stats, setStats] = useState<InvestmentsStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<InvestmentsStats>('/analytics/investments/stats')
      .then(({ data }) => setStats(data))
      .catch((err: ApiError) => setError(err.message || 'Could not load the dashboard.'));
  }, []);

  const kpi = (label: string) => stats?.kpis.find((k) => k.label === label);
  const fmtKpi = (label: string) => {
    const item = kpi(label);
    if (!item) return '—';
    return item.type === 'money' ? fmtMoney(item.value) : item.value;
  };

  return (
    <>
      <header className="page-header">
        <h1>Investment Management Dashboard</h1>
      </header>
      <p className="muted">A snapshot of the investment portfolio and its risk flags.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {!error && !stats && <p className="muted">Loading…</p>}

      {stats && (
        <div className="chart-grid">
          <div className="card">
            <div className="chart-card-header">
              <h2>Investment Portfolio</h2>
            </div>
            <div className="chart-card-hero">{fmtKpi('Investment Portfolio Value')}</div>
            <BarChart
              data={stats.asset_allocation.map((a) => ({ label: a.label, values: [a.value] }))}
              seriesColors={interpolateColors(GREEN_RAMP_STOPS, stats.asset_allocation.length)}
              formatValue={fmtMoney}
            />
            {stats.asset_allocation.some((a) => a.breach) && (
              <div className="alert alert-warning">
                Outside policy range: {stats.asset_allocation.filter((a) => a.breach).map((a) => a.label).join(', ')}
              </div>
            )}
            {Object.values(stats.investment_risk_flags).some((n) => n > 0) && (
              <div className="alert alert-warning">
                {RISK_FLAG_LABELS.filter(({ key }) => stats.investment_risk_flags[key] > 0)
                  .map(({ key, singular, plural }) => {
                    const count = stats.investment_risk_flags[key];

                    return `${count} ${count === 1 ? singular : plural}`;
                  })
                  .join(', ')}
              </div>
            )}
            {hasPermission('investments.reports.view') && (
              <div className="chart-card-footer">
                <Link to="/reports/investments/allocation">View full report →</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
